package se.mittpass.web.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.events.MouseEvent
import kotlinx.browser.window
import se.mittpass.shared.api.AbsenceRequestDto
import se.mittpass.shared.api.SlotDto
import se.mittpass.shared.api.StaffMemberDto
import kotlin.math.max
import kotlin.math.min
import kotlin.math.roundToInt



internal data class GanttDragState(
    val slot: SlotDto,
    val initialMouseX: Double,
    val initialMouseY: Double,
    val currentDeltaX: Double,
    val currentDeltaY: Double,
    val mode: GanttDragMode
)

internal enum class GanttDragMode { MOVE, RESIZE_LEFT, RESIZE_RIGHT }

/**
 * Unified Gantt Canvas Component for Compose HTML / KMP Web.
 * Provides a single continuous 2D timeline matrix where employees are rows (Y-axis)
 * and dates/hours stretch horizontally (X-axis). Work shifts and vacation ribbons
 * sit directly side-by-side on the exact same row per employee.
 */
@Composable
fun UnifiedGanttPlanner(
    startDate: String,
    endDate: String,
    staff: List<StaffMemberDto>,
    slots: List<SlotDto>,
    absences: List<AbsenceRequestDto>,
    isManager: Boolean,
    currentUserId: Long?,
    onSlotClick: (SlotDto) -> Unit,
    onAbsenceClick: (AbsenceRequestDto) -> Unit,
    onCreateSlot: (day: String, startMin: Int, endMin: Int, staffId: Long?) -> Unit,
    onUpdateSlotTime: (slotId: Long, newDay: String, newStartTime: String, newEndTime: String, newStaffId: Long?) -> Unit,
    onClaimSlot: (Long) -> Unit,
    onUnclaimSlot: (Long) -> Unit,
    onDayUnassignedClick: ((day: String, slots: List<SlotDto>) -> Unit)? = null
) {
    var searchQuery by remember { mutableStateOf("") }
    var filterRole by remember { mutableStateOf<String?>(null) }
    var dragState by remember { mutableStateOf<GanttDragState?>(null) }
    var lastDragEndTime by remember { mutableStateOf(0.0) }

    var isPanning by remember { mutableStateOf(false) }
    var panStartX by remember { mutableStateOf(0.0) }
    var panStartY by remember { mutableStateOf(0.0) }
    var panStartScrollLeft by remember { mutableStateOf(0.0) }
    var panStartScrollTop by remember { mutableStateOf(0.0) }
    var lastPanEndTime by remember { mutableStateOf(0.0) }

    val handleSlotClick: (SlotDto) -> Unit = { slot ->
        val now = kotlinx.browser.window.performance.now()
        if (now - lastDragEndTime > 400.0) {
            onSlotClick(slot)
        }
    }

    var windowWidth by remember { mutableStateOf(kotlinx.browser.window.innerWidth) }
    DisposableEffect(Unit) {
        val handler: (dynamic) -> Unit = { windowWidth = kotlinx.browser.window.innerWidth }
        kotlinx.browser.window.addEventListener("resize", handler)
        onDispose { kotlinx.browser.window.removeEventListener("resize", handler) }
    }

    val isMobile = windowWidth < 768
    val staffSidebarWidthPx = if (isMobile) 115.0 else 175.0
    val headerHeightPx = 40.0
    val defaultRowHeightPx = 48.0

    val filteredStaff = remember(staff, searchQuery, filterRole) {
        staff.filter { member ->
            val name = "${member.firstName ?: ""} ${member.lastName ?: ""}".ifEmpty { member.email }
            val matchesSearch = searchQuery.isBlank() || name.contains(searchQuery, ignoreCase = true) || member.email.contains(searchQuery, ignoreCase = true)
            val matchesRole = filterRole == null || member.role.equals(filterRole, ignoreCase = true)
            matchesSearch && matchesRole
        }
    }

    val daysList = remember(startDate, endDate) {
        val list = mutableListOf<String>()
        var curr = startDate
        while (curr <= endDate) {
            list.add(curr)
            curr = addDays(curr, 1)
        }
        list
    }

    // Dynamic responsive dayWidth: fill container on desktop, min 90px on small screens
    val dayWidthPx = remember(windowWidth, daysList.size, staffSidebarWidthPx) {
        val approxContainerWidth = (windowWidth - 70.0).coerceAtLeast(320.0)
        val availableForDays = approxContainerWidth - staffSidebarWidthPx
        val count = daysList.size.coerceAtLeast(1)
        val calculated = availableForDays / count
        maxOf(if (isMobile) 72.0 else 95.0, calculated)
    }

    val totalTimelineWidthPx = daysList.size * dayWidthPx

    // Global Window Mouse Listeners for Drag & Resize
    val activeDragRef = remember { mutableStateOf<GanttDragState?>(null) }
    activeDragRef.value = dragState

    val isDragging = dragState != null
    DisposableEffect(isDragging) {
        if (isDragging) {
            val moveListener: (org.w3c.dom.events.Event) -> Unit = { evt ->
                val activeDrag = activeDragRef.value
                if (activeDrag != null) {
                    val mouseEvt = evt as MouseEvent
                    val dx = mouseEvt.clientX - activeDrag.initialMouseX
                    val dy = mouseEvt.clientY - activeDrag.initialMouseY
                    dragState = activeDrag.copy(currentDeltaX = dx, currentDeltaY = dy)
                }
            }
            val upListener: (org.w3c.dom.events.Event) -> Unit = { evt ->
                val activeDrag = activeDragRef.value
                if (activeDrag != null) {
                    val mouseEvt = evt as MouseEvent
                    val finalDx = mouseEvt.clientX - activeDrag.initialMouseX
                    val finalDy = mouseEvt.clientY - activeDrag.initialMouseY

                    val minutesPerPixel = 1440.0 / dayWidthPx
                    val deltaMinutes = (finalDx * minutesPerPixel / 15.0).roundToInt() * 15

                    val slot = activeDrag.slot
                    val origStartMin = parseMinutes(slot.startTime)
                    val origEndMin = parseMinutes(slot.endTime)

                    val hasMoved = kotlin.math.abs(finalDx) > 3.0 || kotlin.math.abs(finalDy) > 3.0
                    if (!hasMoved) {
                        onSlotClick(slot)
                    } else {
                        lastDragEndTime = kotlinx.browser.window.performance.now()

                        when (activeDrag.mode) {
                            GanttDragMode.MOVE -> {
                                val dropTarget = kotlinx.browser.document.elementFromPoint(mouseEvt.clientX.toDouble(), mouseEvt.clientY.toDouble())
                                var hitStaffId: Long? = slot.assignedToUserId
                                var hitDay: String? = null
                                var foundStaff = false
                                var foundDay = false

                                var curr: org.w3c.dom.Element? = dropTarget
                                while (curr != null) {
                                    if (!foundStaff && curr.hasAttribute("data-staff-id")) {
                                        val attrVal = curr.getAttribute("data-staff-id")
                                        hitStaffId = if (attrVal == "unassigned" || attrVal == "null" || attrVal.isNullOrEmpty()) null else attrVal.toLongOrNull()
                                        foundStaff = true
                                    }
                                    if (!foundDay && curr.hasAttribute("data-day")) {
                                        val attrVal = curr.getAttribute("data-day")
                                        if (!attrVal.isNullOrEmpty()) {
                                            hitDay = attrVal
                                            foundDay = true
                                        }
                                    }
                                    curr = curr.parentElement
                                }

                                val newDay = if (foundDay && hitDay != null) {
                                    hitDay
                                } else {
                                    val dayShift = (finalDx / dayWidthPx).roundToInt()
                                    addDays(slot.day, dayShift)
                                }

                                val newStaffId = if (foundStaff) {
                                    hitStaffId
                                } else {
                                    val origRowIndex = if (slot.assignedToUserId == null) 0 else {
                                        val idx = filteredStaff.indexOfFirst { it.userId == slot.assignedToUserId }
                                        if (idx >= 0) idx + 1 else 0
                                    }
                                    val staffRowIndexChange = (finalDy / defaultRowHeightPx).roundToInt()
                                    val targetRowIndex = (origRowIndex + staffRowIndexChange).coerceIn(0, filteredStaff.size)
                                    if (targetRowIndex == 0) null else filteredStaff.getOrNull(targetRowIndex - 1)?.userId
                                }

                                if (newStaffId != null) {
                                    val hasOverlap = slots.any { other ->
                                        other.id != slot.id &&
                                        other.assignedToUserId == newStaffId &&
                                        other.day == newDay &&
                                        maxOf(origStartMin, parseMinutes(other.startTime)) < minOf(origEndMin, parseMinutes(other.endTime))
                                    }
                                    if (!hasOverlap) {
                                        onUpdateSlotTime(slot.id, newDay, slot.startTime, slot.endTime, newStaffId)
                                    }
                                } else {
                                    onUpdateSlotTime(slot.id, newDay, slot.startTime, slot.endTime, newStaffId)
                                }
                            }
                            GanttDragMode.RESIZE_LEFT -> {
                                val newStartMin = (origStartMin + deltaMinutes).coerceIn(0, origEndMin - 15)
                                onUpdateSlotTime(slot.id, slot.day, formatMinutes(newStartMin), slot.endTime, slot.assignedToUserId)
                            }
                            GanttDragMode.RESIZE_RIGHT -> {
                                val newEndMin = (origEndMin + deltaMinutes).coerceIn(origStartMin + 15, 1440)
                                onUpdateSlotTime(slot.id, slot.day, slot.startTime, formatMinutes(newEndMin), slot.assignedToUserId)
                            }
                        }
                    }
                    dragState = null
                }
            }

            window.addEventListener("mousemove", moveListener)
            window.addEventListener("mouseup", upListener)

            onDispose {
                window.removeEventListener("mousemove", moveListener)
                window.removeEventListener("mouseup", upListener)
            }
        } else {
            onDispose { }
        }
    }

    DisposableEffect(isPanning) {
        if (isPanning) {
            val moveListener: (org.w3c.dom.events.Event) -> Unit = { evt ->
                val mouseEvt = evt as MouseEvent
                val dx = mouseEvt.clientX.toDouble() - panStartX
                val dy = mouseEvt.clientY.toDouble() - panStartY
                if (kotlin.math.abs(dx) > 12.0) {
                    lastPanEndTime = kotlinx.browser.window.performance.now()
                }
                val container = kotlinx.browser.document.getElementById("gantt-scroll-container") as? org.w3c.dom.HTMLElement
                if (container != null) {
                    container.scrollLeft = panStartScrollLeft - dx
                    container.scrollTop = panStartScrollTop - dy
                }
            }
            val upListener: (org.w3c.dom.events.Event) -> Unit = {
                isPanning = false
            }

            kotlinx.browser.window.addEventListener("mousemove", moveListener)
            kotlinx.browser.window.addEventListener("mouseup", upListener)

            onDispose {
                kotlinx.browser.window.removeEventListener("mousemove", moveListener)
                kotlinx.browser.window.removeEventListener("mouseup", upListener)
            }
        } else {
            onDispose { }
        }
    }

    var showInfoNotice by remember {
        mutableStateOf(
            try {
                kotlinx.browser.window.localStorage.getItem("mittpass_hide_gantt_notice") == "false"
            } catch (e: Exception) { false }
        )
    }

    Div({
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("gap", "0.85rem")
            property("width", "100%")
        }
    }) {
        // ── STANDALONE INFORMATIONAL NOTICE CARD (ABOVE GANTT TABLE) ──
        if (showInfoNotice) {
            Div({
                style {
                    property("background", "var(--rp-overlay)")
                    property("border", "1px solid var(--rp-info-border)")
                    property("border-radius", "16px")
                    property("padding", "1rem 1.25rem")
                    property("color", "var(--rp-text)")
                    property("box-shadow", "0 4px 14px rgba(79,70,229,0.06)")
                }
            }) {
                Div({
                    style {
                        property("display", "flex")
                        property("justify-content", "space-between")
                        property("align-items", "center")
                        property("margin-bottom", "0.6rem")
                    }
                }) {
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                        FaIcon("fa-solid fa-lightbulb", size = "1.05rem", color = "var(--rp-pine)")
                        H4({
                            style {
                                property("margin", "0")
                                property("font-size", "0.92rem")
                                property("font-weight", "800")
                                property("color", "var(--rp-text)")
                            }
                        }) {
                            Text(if (isManager) "Så här använder du schemaplaneringen" else "Så här söker du pass i schemat")
                        }
                    }

                    Button({
                        onClick {
                            showInfoNotice = false
                            try {
                                kotlinx.browser.window.localStorage.setItem("mittpass_hide_gantt_notice", "true")
                            } catch (e: Exception) { e.printStackTrace() }
                        }
                        style {
                            property("border", "none")
                            property("background", "var(--rp-surface)")
                            property("color", "var(--rp-pine)")
                            property("border-radius", "50px")
                            property("padding", "0.25rem 0.65rem")
                            property("font-size", "0.75rem")
                            property("font-weight", "700")
                            property("cursor", "pointer")
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.3rem")
                            property("font-family", "inherit")
                            property("box-shadow", "0 2px 6px rgba(0,0,0,0.05)")
                        }
                    }) {
                        Text("✕ Dölj notis")
                    }
                }

                if (isManager) {
                    Div({
                        style {
                            property("display", "grid")
                            property("grid-template-columns", "repeat(auto-fit, minmax(230px, 1fr))")
                            property("gap", "0.75rem")
                            property("font-size", "0.82rem")
                            property("color", "var(--rp-pine)")
                            property("line-height", "1.4")
                        }
                    }) {
                        Div({ style { property("display", "flex"); property("align-items", "flex-start"); property("gap", "0.45rem") } }) {
                            Span({ style { property("font-weight", "800"); property("color", "var(--rp-pine)") } }) { Text("•") }
                            Div {
                                Span({ style { property("font-weight", "800") } }) { Text("Skapa pass: ") }
                                Text("Klicka på en tom yta i kalendern där du vill lägga till ett arbetspass.")
                            }
                        }

                        Div({ style { property("display", "flex"); property("align-items", "flex-start"); property("gap", "0.45rem") } }) {
                            Span({ style { property("font-weight", "800"); property("color", "var(--rp-pine)") } }) { Text("•") }
                            Div {
                                Span({ style { property("font-weight", "800") } }) { Text("Redigera & Tilldela: ") }
                                Text("Klicka på ett pass för att ändra arbetstider eller välja vem som ska jobba.")
                            }
                        }

                        Div({ style { property("display", "flex"); property("align-items", "flex-start"); property("gap", "0.45rem") } }) {
                            Span({ style { property("font-weight", "800"); property("color", "var(--rp-pine)") } }) { Text("•") }
                            Div {
                                Span({ style { property("font-weight", "800") } }) { Text("Flytta pass (Dra & släpp): ") }
                                Text("Håll in musknappen på ett pass och dra det till en annan dag eller person för att flytta det direkt.")
                            }
                        }
                    }
                } else {
                    Div({ style { property("font-size", "0.82rem"); property("color", "var(--rp-pine)") } }) {
                        Span({ style { property("font-weight", "800") } }) { Text("Ansök om pass: ") }
                        Text("Klicka på ett otilldelat pass i schemat och tryck på 'Anmäl intresse' för att skicka in din ansökan.")
                    }
                }
            }
        }

        // ── MAIN GANTT PLANNER TABLE CARD (Fluent 2 Surface) ──
        Div({
            style {
                property("display", "flex")
                property("flex-direction", "column")
                property("width", "100%")
                property("background", "var(--rp-surface)")
                property("border-radius", "8px")
                property("border", "1px solid var(--rp-hl-med)")
                property("overflow", "hidden")
                property("box-shadow", "0 2px 4px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)")
            }
        }) {
            GanttToolbar(
                searchQuery = searchQuery,
                onSearchChange = { searchQuery = it },
                filterRole = filterRole,
                onFilterRoleChange = { filterRole = it },
                showInfoNotice = showInfoNotice,
                onToggleInfoNotice = { showInfoNotice = it }
            )

        Div({
            id("gantt-scroll-container")
            style {
                property("position", "relative")
                property("overflow", "auto")
                property("max-height", "75vh")
                property("-webkit-overflow-scrolling", "touch")
                property("cursor", if (isPanning) "grabbing" else "grab")
                property("background", "var(--rp-surface)")
            }
            onMouseDown { evt ->
                val me = evt.nativeEvent.unsafeCast<MouseEvent>()
                val container = kotlinx.browser.document.getElementById("gantt-scroll-container") as? org.w3c.dom.HTMLElement
                if (container != null && dragState == null) {
                    isPanning = true
                    panStartX = me.clientX.toDouble()
                    panStartY = me.clientY.toDouble()
                    panStartScrollLeft = container.scrollLeft
                    panStartScrollTop = container.scrollTop
                }
            }
        }) {
            Div({
                style {
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("min-width", "${staffSidebarWidthPx + totalTimelineWidthPx}px")
                    property("width", "${staffSidebarWidthPx + totalTimelineWidthPx}px")
                    property("position", "relative")
                }
            }) {
                // Sticky Header Row (Fluent 2 Mica / Acrylic top bar)
                Div({
                    style {
                        property("display", "flex")
                        property("position", "sticky")
                        property("top", "0px")
                        property("z-index", "35")
                        property("height", "${headerHeightPx}px")
                        property("background", "var(--rp-surface)")
                        property("backdrop-filter", "blur(8px)")
                        property("-webkit-backdrop-filter", "blur(8px)")
                        property("border-bottom", "1px solid var(--rp-hl-med)")
                    }
                }) {
                    // Top-Left Sticky Corner
                    Div({
                        style {
                            property("position", "sticky")
                            property("left", "0px")
                            property("top", "0px")
                            property("z-index", "40")
                            property("width", "${staffSidebarWidthPx}px")
                            property("min-width", "${staffSidebarWidthPx}px")
                            property("max-width", "${staffSidebarWidthPx}px")
                            property("flex-shrink", "0")
                            property("height", "${headerHeightPx}px")
                            property("background", "var(--rp-overlay)")
                            property("backdrop-filter", "blur(8px)")
                            property("-webkit-backdrop-filter", "blur(8px)")
                            property("display", "flex")
                            property("align-items", "center")
                            property("padding", "0 0.85rem")
                            property("font-weight", "600")
                            property("font-size", "0.8rem")
                            property("color", "var(--rp-text)")
                            property("letter-spacing", "0.2px")
                            property("border-right", "1px solid var(--rp-hl-med)")
                            property("box-sizing", "border-box")
                        }
                    }) {
                        Text("Personal & Roll")
                    }

                    // Timeline Day Headers
                    Div({
                        style {
                            property("display", "flex")
                            property("height", "${headerHeightPx}px")
                            property("width", "${totalTimelineWidthPx}px")
                            property("min-width", "${totalTimelineWidthPx}px")
                            property("flex-shrink", "0")
                        }
                    }) {
                        daysList.forEach { dayStr ->
                            val isWeekend = isWeekendDay(dayStr)
                            val isToday = dayStr == todayLocalDate()

                            Div({
                                style {
                                    property("width", "${dayWidthPx}px")
                                    property("min-width", "${dayWidthPx}px")
                                    property("max-width", "${dayWidthPx}px")
                                    property("flex-shrink", "0")
                                    property("height", "100%")
                                    property("background", if (isToday) "var(--rp-info-bg)" else if (isWeekend) "var(--rp-overlay)" else "transparent")
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("justify-content", "center")
                                    property("gap", "0.4rem")
                                    property("box-sizing", "border-box")
                                    property("border-right", "1px solid var(--rp-hl-med)")
                                    if (isToday) property("border-bottom", "2.5px solid var(--rp-pine)")
                                }
                            }) {
                                Span({
                                    style {
                                        property("font-size", "0.72rem")
                                        property("font-weight", "600")
                                        property("color", if (isToday) "var(--rp-pine)" else "var(--rp-subtle)")
                                        property("text-transform", "uppercase")
                                        property("letter-spacing", "0.5px")
                                    }
                                }) {
                                    Text(getDayOfWeekShort(dayStr))
                                }
                                Span({
                                    style {
                                        property("font-size", "0.82rem")
                                        property("font-weight", "700")
                                        property("color", if (isToday) "var(--rp-pine)" else "var(--rp-text)")
                                    }
                                }) {
                                    Text(formatDayMonthShort(dayStr))
                                }
                            }
                        }
                    }
                }

                // Grid Canvas Body
                Div({
                    style {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("position", "relative")
                    }
                }) {
                // 1. Unassigned Shifts Row (Otilldelade Pass)
                GanttStaffRow(
                    staffMember = null,
                    rowIndex = 0,
                    daysList = daysList,
                    dayWidthPx = dayWidthPx,
                    slots = slots.filter { it.assignedToUserId == null },
                    absences = emptyList(),
                    rowHeightPx = defaultRowHeightPx,
                    sidebarWidthPx = staffSidebarWidthPx,
                    isManager = isManager,
                    currentUserId = currentUserId,
                    dragState = dragState,
                    lastPanEndTime = lastPanEndTime,
                    onSlotClick = handleSlotClick,
                    onAbsenceClick = onAbsenceClick,
                    onCreateSlot = onCreateSlot,
                    onDayUnassignedClick = onDayUnassignedClick,
                    onStartDrag = { slot, mode, clientX, clientY ->
                        dragState = GanttDragState(slot, clientX, clientY, 0.0, 0.0, mode)
                    }
                )

                // 2. Individual Staff Rows with Alternating Zebra Backgrounds
                filteredStaff.forEachIndexed { index, member ->
                    val memberSlots = slots.filter { it.assignedToUserId == member.userId }
                    val memberAbsences = absences.filter { it.userId == member.userId }

                    GanttStaffRow(
                        staffMember = member,
                        rowIndex = index + 1,
                        daysList = daysList,
                        dayWidthPx = dayWidthPx,
                        slots = memberSlots,
                        absences = memberAbsences,
                        rowHeightPx = defaultRowHeightPx,
                        sidebarWidthPx = staffSidebarWidthPx,
                        isManager = isManager,
                        currentUserId = currentUserId,
                        dragState = dragState,
                        lastPanEndTime = lastPanEndTime,
                        onSlotClick = handleSlotClick,
                        onAbsenceClick = onAbsenceClick,
                        onCreateSlot = onCreateSlot,
                        onDayUnassignedClick = null,
                        onStartDrag = { slot, mode, clientX, clientY ->
                            dragState = GanttDragState(slot, clientX, clientY, 0.0, 0.0, mode)
                        }
                    )
                }
            }
        }
    }
}
}
}


@Composable
private fun GanttStaffRow(
    staffMember: StaffMemberDto?,
    rowIndex: Int,
    daysList: List<String>,
    dayWidthPx: Double,
    slots: List<SlotDto>,
    absences: List<AbsenceRequestDto>,
    rowHeightPx: Double,
    sidebarWidthPx: Double,
    isManager: Boolean,
    currentUserId: Long?,
    dragState: GanttDragState?,
    lastPanEndTime: Double,
    onSlotClick: (SlotDto) -> Unit,
    onAbsenceClick: (AbsenceRequestDto) -> Unit,
    onCreateSlot: (day: String, startMin: Int, endMin: Int, staffId: Long?) -> Unit,
    onDayUnassignedClick: ((day: String, slots: List<SlotDto>) -> Unit)?,
    onStartDrag: (SlotDto, GanttDragMode, Double, Double) -> Unit
) {
    val totalTimelineWidthPx = daysList.size * dayWidthPx
    val isUnassignedRow = staffMember == null
    val isOddRow = rowIndex % 2 == 1

    val maxSlotsInAnyDay = remember(slots) {
        val counts = slots.groupBy { it.day }.map { it.value.size }
        counts.maxOrNull() ?: 0
    }

    // Check if any day has both an absence and at least one slot
    val hasAbsenceAndSlotCollision = remember(slots, absences) {
        if (absences.isEmpty() || slots.isEmpty()) false
        else {
            val slotDays = slots.map { it.day }.toSet()
            // Check if absence spans any day with a slot
            absences.any { absence ->
                daysList.any { day ->
                    day >= absence.startDate && day <= absence.endDate && slotDays.contains(day)
                }
            }
        }
    }

    // Uniform row height: 48px baseline for all rows (both staff and unassigned).
    // Only expand if there are stacked multiple slots (>1) or slot collides with absence.
    val dynamicRowHeightPx = if (isUnassignedRow) {
        48.0
    } else {
        if (hasAbsenceAndSlotCollision) {
            val slotsCount = max(1, maxSlotsInAnyDay)
            30.0 + slotsCount * 42.0 + 5.0
        } else if (maxSlotsInAnyDay > 1) {
            5.0 + maxSlotsInAnyDay * 42.0 + 5.0
        } else {
            48.0
        }
    }

    val isCurrentUser = staffMember != null && staffMember.userId == currentUserId

    val cellBg = when {
        isUnassignedRow -> "var(--rp-warn-bg)"
        isCurrentUser -> "var(--rp-info-bg)"
        isOddRow -> "var(--rp-surface)"
        else -> "var(--rp-surface)"
    }

    Div({
        attr("data-staff-id", if (isUnassignedRow) "unassigned" else "${staffMember?.userId}")
        style {
            property("display", "flex")
            property("height", "${dynamicRowHeightPx}px")
            property("box-sizing", "border-box")
            property("background", cellBg)
            property("border-bottom", "1px solid var(--rp-hl-med)")
        }
    }) {
        // Sticky Left Staff Sidebar Cell (Fluent 2 neutral elevation)
        Div({
            style {
                property("position", "sticky")
                property("left", "0px")
                property("z-index", "25")
                property("width", "${sidebarWidthPx}px")
                property("min-width", "${sidebarWidthPx}px")
                property("max-width", "${sidebarWidthPx}px")
                property("height", "100%")
                property("background", cellBg)
                property("display", "flex")
                property("align-items", "center")
                property("padding", "0 0.75rem")
                property("box-sizing", "border-box")
                property("border-right", "1px solid var(--rp-hl-med)")
                if (isCurrentUser) property("border-left", "3.5px solid var(--rp-pine)")
            }
        }) {
            if (isUnassignedRow) {
                Div({
                    style {
                        property("width", "28px")
                        property("height", "28px")
                        property("border-radius", "4px")
                        property("background", "var(--rp-warn-bg)")
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("margin-right", "0.5rem")
                        property("flex-shrink", "0")
                        property("border", "1px solid var(--rp-warn-border)")
                    }
                }) {
                    FaIcon("fa-solid fa-circle-question", size = "0.85rem", color = "var(--rp-warn-text)")
                }
                Div({
                    style {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("overflow", "hidden")
                    }
                }) {
                    Span({
                        style {
                            property("font-weight", "600")
                            property("font-size", "0.82rem")
                            property("color", "var(--rp-warn-text)")
                            property("white-space", "nowrap")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                        }
                    }) {
                        Text("Otilldelade Pass")
                    }
                    Span({
                        style {
                            property("font-size", "0.68rem")
                            property("color", "var(--rp-warn-text)")
                            property("font-weight", "500")
                            property("white-space", "nowrap")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                        }
                    }) {
                        Text("Sökes av personal")
                    }
                }
            } else {
                val savedAvatar = staffMember?.userId?.let { uid -> kotlinx.browser.localStorage.getItem("mittpass_avatar_$uid") }
                val memberName = "${staffMember?.firstName ?: ""} ${staffMember?.lastName ?: ""}".ifEmpty { staffMember?.email ?: "Anställd" }
                val initial = memberName.take(1).uppercase()
                val roleName = staffMember?.role ?: "Personal"
                val memberColorScheme = getEmployeeColorScheme(staffMember?.color, staffMember?.userId)

                Div({
                    style {
                        property("width", "28px")
                        property("height", "28px")
                        property("border-radius", "50%")
                        if (!savedAvatar.isNullOrBlank()) property("background-image", "url('$savedAvatar')")
                        if (!savedAvatar.isNullOrBlank()) property("background-size", "cover")
                        if (!savedAvatar.isNullOrBlank()) property("background-position", "center")
                        if (savedAvatar.isNullOrBlank()) property("background", memberColorScheme.primary)
                        property("color", "#FFFFFF")
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("font-weight", "700")
                        property("font-size", "0.78rem")
                        property("margin-right", "0.5rem")
                        property("border", if (isCurrentUser) "1.5px solid var(--rp-pine)" else "1px solid ${memberColorScheme.border}")
                        property("flex-shrink", "0")
                        property("overflow", "hidden")
                    }
                }) {
                    if (savedAvatar.isNullOrBlank()) {
                        Text(initial)
                    }
                }

                Div({
                    style {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("overflow", "hidden")
                    }
                }) {
                    Div({
                        style {
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.35rem")
                        }
                    }) {
                        Span({
                            style {
                                property("font-weight", "600")
                                property("font-size", "0.82rem")
                                property("color", if (isCurrentUser) "var(--rp-pine)" else "var(--rp-text)")
                                property("white-space", "nowrap")
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                            }
                        }) {
                            Text(memberName)
                        }
                        if (isCurrentUser) {
                            Span({
                                style {
                                    property("background", "var(--rp-pine)")
                                    property("color", "#FFFFFF")
                                    property("font-size", "0.6rem")
                                    property("font-weight", "700")
                                    property("padding", "0.1rem 0.35rem")
                                    property("border-radius", "3px")
                                    property("letter-spacing", "0.02em")
                                    property("flex-shrink", "0")
                                }
                            }) {
                                Text("Du")
                            }
                        }
                    }
                    Span({
                        style {
                            property("font-size", "0.68rem")
                            property("color", if (isCurrentUser) "var(--rp-pine)" else "var(--rp-subtle)")
                            property("white-space", "nowrap")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                        }
                    }) {
                        Text(roleName.replaceFirstChar { it.uppercase() })
                    }
                }
            }
        }

        // Timeline Row Grid Canvas
        Div({
            style {
                property("position", "relative")
                property("width", "${totalTimelineWidthPx}px")
                property("min-width", "${totalTimelineWidthPx}px")
                property("height", "100%")
            }
        }) {
            // Background Day Grid Cells (Fluent 2 grid lines)
            daysList.forEachIndexed { idx, dayStr ->
                val leftPx = idx * dayWidthPx
                val isWeekend = isWeekendDay(dayStr)
                val isToday = dayStr == todayLocalDate()

                val dayCellBg = when {
                    isToday -> if (isOddRow) "var(--rp-info-bg)" else "var(--rp-info-bg)"
                    isUnassignedRow -> "var(--rp-warn-bg)"
                    isOddRow -> "var(--rp-surface)"
                    isWeekend -> "var(--rp-surface)"
                    else -> "var(--rp-surface)"
                }

                var lastCellCreateTime by remember { mutableStateOf(0.0) }
                val triggerCellCreate: () -> Unit = {
                    if (isManager) {
                        val now = kotlinx.browser.window.performance.now()
                        if (now - lastPanEndTime > 300.0 && now - lastCellCreateTime > 400.0) {
                            lastCellCreateTime = now
                            val defaultStartMin = 540 // 09:00
                            val defaultEndMin = 1020 // 17:00
                            val targetStaffId = if (isUnassignedRow) null else staffMember?.userId
                            onCreateSlot(dayStr, defaultStartMin, defaultEndMin, targetStaffId)
                        }
                    }
                }

                Div({
                    attr("data-day", dayStr)
                    attr("data-staff-id", if (isUnassignedRow) "unassigned" else "${staffMember?.userId}")
                    attr("data-testid", "gantt-cell-${idx}")
                    if (isManager) {
                        onMouseDown { triggerCellCreate() }
                    }
                    style {
                        property("position", "absolute")
                        property("left", "${leftPx}px")
                        property("top", "0")
                        property("width", "${dayWidthPx}px")
                        property("height", "100%")
                        property("background", dayCellBg)
                        property("border-right", "1px solid var(--rp-hl-med)")
                        property("box-sizing", "border-box")
                        if (isManager) property("cursor", "pointer")
                    }
                })
            }

            // Layer 0: Absence Bars
            if (!isUnassignedRow) {
                absences.forEach { absence ->
                    val bounds = calculateVacationBounds(absence, daysList.first(), daysList.last(), dayWidthPx)
                    if (bounds != null) {
                        VacationSpanningBar(
                            absence = absence,
                            leftPx = bounds.first,
                            widthPx = bounds.second,
                            topPx = if (hasAbsenceAndSlotCollision) 4.0 else 12.0,
                            onClick = { onAbsenceClick(absence) }
                        )
                    }
                }
            }

            // Layer 1: Shift Cards
            if (isUnassignedRow) {
                daysList.forEachIndexed { dayIdx, dayStr ->
                    val daySlots = slots.filter { it.day == dayStr }.sortedBy { parseMinutes(it.startTime) }
                    val baseLeft = dayIdx * dayWidthPx + 2.0
                    val widthPx = dayWidthPx - 4.0
                    val baseTop = 5.0
                    val cardHeight = 38.0

                    if (daySlots.size == 1) {
                        val slot = daySlots.first()
                        val currentDrag = dragState
                        val isBeingDragged = currentDrag != null && currentDrag.slot.id == slot.id
                        val renderLeft = if (isBeingDragged) baseLeft + currentDrag.currentDeltaX else baseLeft
                        val renderTop = if (isBeingDragged) baseTop + currentDrag.currentDeltaY else baseTop

                        key(slot.id, slot.interestedUsers, currentUserId) {
                            ShiftCardItem(
                                slot = slot,
                                leftPx = renderLeft,
                                topPx = renderTop,
                                widthPx = widthPx,
                                heightPx = cardHeight,
                                isManager = isManager,
                                currentUserId = currentUserId,
                                isDragged = isBeingDragged,
                                staffMember = staffMember,
                                onClick = { onSlotClick(slot) },
                                onStartDrag = { mode, clientX, clientY -> onStartDrag(slot, mode, clientX, clientY) }
                            )
                        }
                    } else if (daySlots.size > 1) {
                        var cardClickStart by remember { mutableStateOf(0.0) }
                        val hasAnyApplicants = daySlots.any { it.interestedUsers.isNotEmpty() }
                        val btnBg = if (hasAnyApplicants) "var(--rp-info-bg)" else "var(--rp-warn-bg)"
                        val btnBorder = if (hasAnyApplicants) "var(--rp-pine)" else "var(--rp-warn-border)"
                        val btnText = if (hasAnyApplicants) "var(--rp-pine)" else "var(--rp-warn-text)"

                        Div({
                            attr("data-testid", "unassigned-badge-$dayStr")
                            style {
                                property("position", "absolute")
                                property("left", "${baseLeft}px")
                                property("top", "${baseTop}px")
                                property("width", "${widthPx}px")
                                property("height", "${cardHeight}px")
                                property("background", btnBg)
                                property("border", "1px solid $btnBorder")
                                property("border-left", "4px solid $btnBorder")
                                property("border-radius", "6px")
                                property("z-index", "15")
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                                property("gap", "0.35rem")
                                property("padding", "0 0.5rem")
                                property("box-sizing", "border-box")
                                property("cursor", "pointer")
                                property("user-select", "none")
                                property("box-shadow", "0 1px 3px rgba(0,0,0,0.05)")
                                property("transition", "transform 0.15s ease")
                            }
                            onMouseDown { evt ->
                                evt.preventDefault()
                                val me = evt.nativeEvent.unsafeCast<MouseEvent>()
                                cardClickStart = me.clientX.toDouble()
                            }
                            onClick { evt ->
                                evt.stopPropagation()
                                if (onDayUnassignedClick != null) {
                                    onDayUnassignedClick(dayStr, daySlots)
                                } else if (daySlots.isNotEmpty()) {
                                    onSlotClick(daySlots.first())
                                }
                            }
                        }) {
                            FaIcon("fa-solid fa-layer-group", size = "0.75rem", color = btnText)
                            Span({
                                style {
                                    property("font-size", "0.78rem")
                                    property("font-weight", "600")
                                    property("color", btnText)
                                    property("white-space", "nowrap")
                                }
                            }) {
                                Text("${daySlots.size} pass")
                            }
                        }
                    }
                }
            } else {
                daysList.forEachIndexed { dayIdx, dayStr ->
                    val daySlots = slots.filter { it.day == dayStr }.sortedBy { parseMinutes(it.startTime) }
                    val dayHasAbsence = absences.any { dayStr >= it.startDate && dayStr <= it.endDate }

                    daySlots.forEachIndexed { slotIdx, slot ->
                        val baseLeft = dayIdx * dayWidthPx + 2.0
                        val widthPx = dayWidthPx - 4.0
                        val cardHeight = 38.0
                        val baseTop = (if (dayHasAbsence) 30.0 else 5.0) + slotIdx * 42.0

                        val currentDrag = dragState
                        val isBeingDragged = currentDrag != null && currentDrag.slot.id == slot.id
                        val renderLeft = if (isBeingDragged) baseLeft + currentDrag.currentDeltaX else baseLeft
                        val renderTop = if (isBeingDragged) baseTop + currentDrag.currentDeltaY else baseTop

                        key(slot.id, slot.interestedUsers, currentUserId) {
                            ShiftCardItem(
                                slot = slot,
                                leftPx = renderLeft,
                                topPx = renderTop,
                                widthPx = widthPx,
                                heightPx = cardHeight,
                                isManager = isManager,
                                currentUserId = currentUserId,
                                isDragged = isBeingDragged,
                                staffMember = staffMember,
                                onClick = { onSlotClick(slot) },
                                onStartDrag = { mode, clientX, clientY -> onStartDrag(slot, mode, clientX, clientY) }
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun VacationSpanningBar(
    absence: AbsenceRequestDto,
    leftPx: Double,
    widthPx: Double,
    topPx: Double = 4.0,
    onClick: () -> Unit
) {
    val (bg, borderColor, textColor) = when (absence.status.uppercase()) {
        "APPROVED" -> Triple("var(--rp-ok-bg)", "var(--rp-ok-text)", "var(--rp-ok-text)")
        "PENDING" -> Triple("var(--rp-warn-bg)", "var(--rp-warn-text)", "var(--rp-warn-text)")
        else -> Triple("var(--rp-err-bg)", "var(--rp-err-text)", "var(--rp-err-text)")
    }

    Div({
        style {
            property("position", "absolute")
            property("left", "${leftPx + 4}px")
            property("top", "${topPx}px")
            property("width", "${max(110.0, widthPx - 8)}px")
            property("min-width", "110px")
            property("height", "22px")
            property("background", bg)
            property("border", "1px solid $borderColor")
            property("border-left", "3px solid $borderColor")
            property("border-radius", "4px")
            property("z-index", "12")
            property("display", "flex")
            property("align-items", "center")
            property("padding", "0 0.5rem")
            property("box-sizing", "border-box")
            property("cursor", "pointer")
            property("user-select", "none")
            property("box-shadow", "0 1px 2px rgba(0,0,0,0.04)")
        }
        onClick { onClick() }
    }) {
        FaIcon("fa-solid fa-umbrella-beach", size = "0.65rem", color = textColor)
        Span({
            style {
                property("font-size", "0.68rem")
                property("font-weight", "600")
                property("color", textColor)
                property("white-space", "nowrap")
                property("overflow", "hidden")
                property("text-overflow", "ellipsis")
                property("margin-left", "0.3rem")
            }
        }) {
            val typeLabel = when (absence.type.lowercase()) { "vacation" -> "Semester"; "sick" -> "Sjukanmälan"; "vab" -> "VAB"; else -> "Frånvaro" }
            val statusLabel = when (absence.status.lowercase()) { "approved" -> "Godkänd"; "pending" -> "Väntar godkännande"; else -> "Avslagen" }
            Text("$typeLabel ($statusLabel)")
        }
    }
}

@Composable
private fun ShiftCardItem(
    slot: SlotDto,
    leftPx: Double,
    topPx: Double,
    widthPx: Double,
    heightPx: Double = 26.0,
    isManager: Boolean,
    currentUserId: Long?,
    isDragged: Boolean,
    staffMember: StaffMemberDto? = null,
    onClick: () -> Unit,
    onStartDrag: (GanttDragMode, Double, Double) -> Unit
) {
    val isUnassigned = slot.assignedToUserId == null
    val applicantCount = slot.interestedUsers.size
    val hasApplicants = isUnassigned && applicantCount > 0
    val hasClaimed = isUnassigned && currentUserId != null && slot.interestedUsers.any { it.id == currentUserId }

    val memberColorScheme = if (!isUnassigned) {
        getEmployeeColorScheme(staffMember?.color, slot.assignedToUserId)
    } else null

    // Fluent 2 card design: Soft tinted surface with crisp left accent border
    val cardBg = when {
        hasClaimed -> "var(--rp-warn-bg)"
        hasApplicants -> "var(--rp-info-bg)"
        isUnassigned -> "var(--rp-warn-bg)"
        memberColorScheme != null -> memberColorScheme.bg
        else -> "var(--rp-info-bg)"
    }
    val textColor = when {
        hasClaimed -> "var(--rp-warn-text)"
        hasApplicants -> "var(--rp-pine)"
        isUnassigned -> "var(--rp-warn-text)"
        else -> "var(--rp-text)"
    }
    val accentColor = when {
        hasClaimed -> "var(--rp-warn-text)"
        hasApplicants -> "var(--rp-pine)"
        isUnassigned -> "var(--rp-warn-text)"
        memberColorScheme != null -> memberColorScheme.primary
        else -> "var(--rp-pine)"
    }
    val borderColor = when {
        hasClaimed -> "var(--rp-warn-border)"
        hasApplicants -> "var(--rp-info-border)"
        isUnassigned -> "var(--rp-warn-border)"
        memberColorScheme != null -> memberColorScheme.border
        else -> "var(--rp-info-border)"
    }
    val effectiveWidth = widthPx

    val startFormatted = slot.startTime.trim().split(":").take(2).joinToString(":")
    val endFormatted = slot.endTime.trim().split(":").take(2).joinToString(":")
    val timeLabel = "$startFormatted–$endFormatted"

    var dragStartX by remember { mutableStateOf(0.0) }
    var dragStartY by remember { mutableStateOf(0.0) }

    Div({
        attr("data-testid", "slot-${slot.id}")
        style {
            property("position", "absolute")
            property("left", "${leftPx}px")
            property("top", "${topPx}px")
            property("width", "${effectiveWidth}px")
            property("height", "${heightPx}px")
            property("background", cardBg)
            property("border", "1px solid $borderColor")
            property("border-left", "3.5px solid $accentColor")
            property("border-radius", "6px")
            property("z-index", if (isDragged) "100" else "10")
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "center")
            property("justify-content", "center")
            property("padding", "0.2rem 0.35rem")
            property("box-sizing", "border-box")
            property("cursor", "pointer")
            property("user-select", "none")
            property("pointer-events", if (isDragged) "none" else "auto")
            property("opacity", if (isDragged) "0.88" else "1.0")
            property("box-shadow", if (isDragged) "0 8px 16px rgba(0,0,0,0.14)" else "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)")
            property("transition", if (isDragged) "none" else "transform 0.1s ease, box-shadow 0.1s ease")
        }
        onMouseDown { evt ->
            evt.preventDefault()
            val me = evt.nativeEvent.unsafeCast<MouseEvent>()
            dragStartX = me.clientX.toDouble()
            dragStartY = me.clientY.toDouble()
            if (isManager) {
                onStartDrag(GanttDragMode.MOVE, me.clientX.toDouble(), me.clientY.toDouble())
            }
        }
        onClick { evt ->
            val me = evt.nativeEvent.unsafeCast<MouseEvent>()
            val dx = me.clientX.toDouble() - dragStartX
            val dy = me.clientY.toDouble() - dragStartY
            val dist = kotlin.math.sqrt(dx * dx + dy * dy)
            if (dist < 6.0) {
                onClick()
            }
        }
    }) {
        Div({
            style {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("gap", "0.25rem")
                property("width", "100%")
                property("overflow", "hidden")
            }
        }) {
            if (hasApplicants) {
                FaIcon("fa-solid fa-user-check", size = "0.7rem", color = "var(--rp-pine)")
            } else if (hasClaimed) {
                FaIcon("fa-solid fa-hand", size = "0.7rem", color = "var(--rp-warn-text)")
            } else if (isUnassigned) {
                FaIcon("fa-solid fa-circle-question", size = "0.7rem", color = "var(--rp-warn-text)")
            }

            Span({
                style {
                    property("font-size", "0.76rem")
                    property("font-weight", "600")
                    property("color", textColor)
                    property("line-height", "1.1")
                    property("white-space", "nowrap")
                    property("letter-spacing", "-0.1px")
                }
            }) {
                Text(timeLabel)
            }
        }

        // Optional subtext pill if unassigned or applicants
        if (hasClaimed || hasApplicants || isUnassigned) {
            val subText = when {
                hasClaimed -> "Sökt"
                hasApplicants -> if (applicantCount == 1) "1 sökande" else "$applicantCount sökande"
                !isManager -> "Tryck för att ta"
                else -> "Otilldelat"
            }
            Span({
                style {
                    property("font-size", "0.62rem")
                    property("font-weight", "600")
                    property("color", textColor)
                    property("opacity", "0.85")
                    property("white-space", "nowrap")
                    property("margin-top", "1px")
                }
            }) { Text(subText) }
        }
    }
}

@Composable
private fun GanttToolbar(
    searchQuery: String,
    onSearchChange: (String) -> Unit,
    filterRole: String?,
    onFilterRoleChange: (String?) -> Unit,
    showInfoNotice: Boolean,
    onToggleInfoNotice: (Boolean) -> Unit
) {
    Div({
        style {
            property("display", "flex")
            property("justify-content", "space-between")
            property("align-items", "center")
            property("padding", "0.5rem 0.85rem")
            property("background", "var(--rp-surface)")
            property("border-bottom", "1px solid var(--rp-hl-med)")
            property("gap", "0.75rem")
            property("flex-wrap", "wrap")
        }
    }) {
        // Search & Role Filters (Fluent 2 Command Bar elements)
        Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem"); property("flex-wrap", "wrap") } }) {
            Div({
                style {
                    property("position", "relative")
                    property("display", "flex")
                    property("align-items", "center")
                }
            }) {
                Span({
                    style {
                        property("position", "absolute")
                        property("left", "0.6rem")
                        property("pointer-events", "none")
                        property("display", "flex")
                        property("align-items", "center")
                    }
                }) {
                    FaIcon("fa-solid fa-magnifying-glass", size = "0.75rem", color = "var(--rp-subtle)")
                }
                Input(type = InputType.Text) {
                    value(searchQuery)
                    onInput { onSearchChange(it.value) }
                    placeholder("Sök personal...")
                    style {
                        property("padding", "0.32rem 0.7rem 0.32rem 1.85rem")
                        property("border-radius", "4px")
                        property("border", "1px solid var(--rp-hl-med)")
                        property("background", "var(--rp-surface)")
                        property("font-size", "0.8rem")
                        property("outline", "none")
                        property("font-family", "inherit")
                        property("color", "var(--rp-text)")
                        property("min-width", "180px")
                    }
                }
            }

            // Segmented filter pill group
            Div({
                style {
                    property("display", "inline-flex")
                    property("background", "var(--rp-hl-med)")
                    property("padding", "2px")
                    property("border-radius", "6px")
                    property("gap", "2px")
                }
            }) {
                listOf(null to "Alla", "manager" to "Managers", "staff" to "Personal").forEach { (role, label) ->
                    val active = filterRole == role
                    Button({
                        onClick { onFilterRoleChange(role) }
                        style {
                            property("padding", "0.22rem 0.65rem")
                            property("border-radius", "4px")
                            property("border", "none")
                            property("background", if (active) "var(--rp-surface)" else "transparent")
                            property("color", if (active) "var(--rp-text)" else "var(--rp-subtle)")
                            property("font-size", "0.72rem")
                            property("font-weight", if (active) "600" else "500")
                            property("cursor", "pointer")
                            if (active) property("box-shadow", "0 1px 2px rgba(0,0,0,0.08)")
                        }
                    }) { Text(label) }
                }
            }
        }

        // Help Toggle Button (Fluent discrete standard button)
        if (!showInfoNotice) {
            Button({
                onClick {
                    onToggleInfoNotice(true)
                    try {
                        kotlinx.browser.window.localStorage.removeItem("mittpass_hide_gantt_notice")
                    } catch (e: Exception) { e.printStackTrace() }
                }
                style {
                    property("padding", "0.25rem 0.65rem")
                    property("border", "1px solid var(--rp-hl-med)")
                    property("background", "var(--rp-surface)")
                    property("color", "var(--rp-text)")
                    property("border-radius", "4px")
                    property("font-size", "0.74rem")
                    property("font-weight", "600")
                    property("cursor", "pointer")
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "0.35rem")
                    property("font-family", "inherit")
                }
            }) {
                FaIcon("fa-solid fa-circle-question", size = "0.75rem", color = "var(--rp-pine)")
                Text("Hjälp & Tips")
            }
        }
    }
}

@Composable
private fun LegendChip(
    color: String,
    borderColor: String? = null,
    textColor: String = "var(--rp-text)",
    label: String
) {
    Div({
        style {
            property("display", "inline-flex")
            property("align-items", "center")
            property("gap", "0.35rem")
            property("font-size", "0.75rem")
            property("font-weight", "700")
            property("color", textColor)
        }
    }) {
        Div({
            style {
                property("width", "12px")
                property("height", "12px")
                property("border-radius", "3px")
                property("background", color)
                if (borderColor != null) property("border", "1px solid $borderColor")
            }
        }) {}
        Text(label)
    }
}

private fun assignLanesForSlots(slots: List<SlotDto>): Map<Long, Int> {
    val laneMap = mutableMapOf<Long, Int>()
    val groupedByDay = slots.groupBy { it.day }
    groupedByDay.forEach { (_, daySlots) ->
        val sorted = daySlots.sortedBy { parseMinutes(it.startTime) }
        val laneEndMinutes = mutableListOf<Int>()
        sorted.forEach { slot ->
            val startM = parseMinutes(slot.startTime)
            val endM = parseMinutes(slot.endTime)
            var placedLane = -1
            for (i in laneEndMinutes.indices) {
                if (laneEndMinutes[i] <= startM) {
                    laneEndMinutes[i] = endM
                    placedLane = i
                    break
                }
            }
            if (placedLane == -1) {
                laneEndMinutes.add(endM)
                placedLane = laneEndMinutes.size - 1
            }
            laneMap[slot.id] = placedLane
        }
    }
    return laneMap
}

private fun calculateShiftBounds(slot: SlotDto, timelineStart: String, dayWidthPx: Double): Pair<Double, Double>? {
    val dayDiff = daysBetween(timelineStart, slot.day)
    if (dayDiff < 0) return null
    val startMin = parseMinutes(slot.startTime).coerceIn(0, 1440)
    val endMin = parseMinutes(slot.endTime).coerceIn(startMin, 1440)
    val durationMin = max(15, endMin - startMin)

    val dayLeftPx = dayDiff * dayWidthPx
    val startOffsetPx = (startMin / 1440.0) * (dayWidthPx - 8.0) + 4.0
    val rawWidthPx = (durationMin / 1440.0) * (dayWidthPx - 8.0)

    val maxAllowedWidthPx = max(30.0, (dayLeftPx + dayWidthPx - 4.0) - (dayLeftPx + startOffsetPx))
    val widthPx = max(75.0, rawWidthPx).coerceAtMost(maxAllowedWidthPx)

    return Pair(dayLeftPx + startOffsetPx, widthPx)
}

private fun calculateVacationBounds(absence: AbsenceRequestDto, timelineStart: String, timelineEnd: String, dayWidthPx: Double): Pair<Double, Double>? {
    val startDiff = daysBetween(timelineStart, absence.startDate)
    val endDiff = daysBetween(timelineStart, absence.endDate)
    val maxVisibleDayIdx = max(0, daysBetween(timelineStart, timelineEnd))

    if (endDiff < 0 || startDiff > maxVisibleDayIdx) return null

    val clampedStart = startDiff.coerceIn(0, maxVisibleDayIdx)
    val clampedEnd = endDiff.coerceIn(0, maxVisibleDayIdx)

    if (clampedStart > clampedEnd) return null

    val leftPx = clampedStart * dayWidthPx
    val widthPx = (clampedEnd - clampedStart + 1) * dayWidthPx

    return Pair(leftPx, widthPx)
}

private fun formatMinutes(min: Int): String {
    val h = (min / 60).coerceIn(0, 23).toString().padStart(2, '0')
    val m = (min % 60).coerceIn(0, 59).toString().padStart(2, '0')
    return "$h:$m"
}

private fun isWeekendDay(dateStr: String): Boolean {
    val d = parseDate(dateStr)
    val day = d.getDay()
    return day == 0 || day == 6
}

private fun getDayOfWeekShort(dateStr: String): String {
    val d = parseDate(dateStr)
    val idx = (d.getDay() + 6) % 7
    return DAY_NAMES_SHORT.getOrElse(idx) { "" }
}

private fun formatDayMonthShort(dateStr: String): String {
    val parts = dateStr.split("-")
    if (parts.size < 3) return dateStr
    val monthNum = parts[1].toIntOrNull() ?: 1
    val dayNum = parts[2].toIntOrNull() ?: 1
    return "$dayNum/${monthNum}"
}

private fun getInitials(first: String?, last: String?, email: String): String {
    val f = first?.firstOrNull()?.uppercaseChar()
    val l = last?.firstOrNull()?.uppercaseChar()
    if (f != null && l != null) return "$f$l"
    if (f != null) return "$f"
    return email.take(2).uppercase()
}
